import { useEffect, useState } from "react";

import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Image,
  ProgressBar,
  Spinner,
  CloseButton,
} from "react-bootstrap";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import axios from "axios";
import API from "../../api/api";

// ---- Yup Validation ----
const FILE_SIZE_MB = 2;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/jpg", "image/webp"];

function TestimonialsModal({
  show,
  handleClose,
  payload,
  setPayload,
  getAllData,
  hostname,
}) {
  // console.log(payload);
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(0);

  const AddTestimonialDB = async (values, { setSubmitting, resetForm }) => {
    try {
      const fd = new FormData();
      fd.append("name", values.name);
      fd.append("role", values.role || "");
      fd.append("text_description", values.text_description);
      fd.append("status", values.status);
      if (
        values.order !== "" &&
        values.order !== null &&
        values.order !== undefined
      ) {
        fd.append("order", values.order);
      }
      if (values.profile_img) {
        fd.append("profile_img", values.profile_img);
      }

      await API.post(`${hostname}/api/testimonials/create-testimonial`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) {
            const p = Math.round((e.loaded * 100) / e.total);
            setProgress(p);
          }
        },
      });

      resetForm();
      setPreview(null);
      setProgress(0);
      getAllData();
      handleClose();
      setPayload({});
    } catch (err) {
      console.error(err);
      // You can toast or show an alert here
    } finally {
      setSubmitting(false);
    }
  };

  const updateTestimonialDB = async (values, { setSubmitting, resetForm }) => {
    try {
      const fd = new FormData();
      fd.append("name", values.name);
      fd.append("role", values.role || "");
      fd.append("text_description", values.text_description);
      fd.append("status", values.status);
      fd.append("testimonial", payload._id);
      if (
        values.order !== "" &&
        values.order !== null &&
        values.order !== undefined
      ) {
        fd.append("order", values.order);
      }
      if (values.profile_img) {
        fd.append("profile_img", values.profile_img);
      }

      await API.post(`${hostname}/api/testimonials/update-testimonial`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) {
            const p = Math.round((e.loaded * 100) / e.total);
            setProgress(p);
          }
        },
      });

      resetForm();
      setPreview(null);
      setProgress(0);
      getAllData();
      handleClose();
      setPayload({});
    } catch (err) {
      console.error(err);
      // You can toast or show an alert here
    } finally {
      setSubmitting(false);
    }
  };

  const deleteEntry = () => {
    API
      .post(`${hostname}/api/testimonials/delete-testimonial`, {
        testimonial_id: payload?._id,
      })
      .then((res) => {
        // console.log(res.data);
        getAllData();
        handleClose();
        toast.warning("Testimonial deleted.", { theme: "colored" });
        setPayload({});
      })
      .catch((err) => {
        console.log(err);
        getAllData();
        if ("response" in err)
          toast.error(err.response.data.error, { theme: "colored" });
        else toast.error("something went wrong!", { theme: "colored" });
      });
  };
  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
    >
      <Modal.Header>
        <Modal.Title>{payload.loadType}</Modal.Title>
        <CloseButton onClick={handleClose} />
      </Modal.Header>
      <Modal.Body>
        {payload.loadType === "Add Testimonial" && (
          <Formik
            initialValues={{
              name: "",
              role: "",
              profile_img: null,
              text_description: "",
              status: "active",
              order: "",
            }}
            validationSchema={Yup.object({
              name: Yup.string()
                .trim()
                .required("Name is required")
                .max(120, "Max 120 characters"),
              role: Yup.string()
                .trim()
                .max(120, "Max 120 characters")
                .nullable(),
              text_description: Yup.string()
                .trim()
                .required("Description is required")
                .max(2000, "Max 2000 characters"),
              status: Yup.string()
                .oneOf(["active", "inactive"])
                .required("Status is required"),
              order: Yup.number()
                .typeError("Order must be a number")
                .integer("Order must be an integer")
                .min(0, "Minimum 0")
                .nullable(),
              profile_img: Yup.mixed()
                .nullable()
                .test(
                  "fileSize",
                  `File size must be ≤ ${FILE_SIZE_MB}MB`,
                  (file) =>
                    !file || (file && file.size <= FILE_SIZE_MB * 1024 * 1024)
                )
                .test(
                  "fileType",
                  "Only JPG, PNG, WEBP allowed",
                  (file) =>
                    !file || (file && ACCEPTED_TYPES.includes(file.type))
                ),
            })}
            onSubmit={AddTestimonialDB}
          >
            {({ values, setFieldValue, isSubmitting, touched, errors }) => (
              <FormikForm>
                <Modal.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Label className="fw-semibold">
                        Name <span className="text-danger">*</span>
                      </Form.Label>
                      <Field
                        name="name"
                        className='form-control'
                        type="text"
                        placeholder="e.g., Jane Doe"
                        isInvalid={touched.name && !!errors.name}
                      />
                      <ErrorMessage
                        name="name"
                        component="div"
                        className="text-danger fs--1 mt-1"
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label className="fw-semibold">Role</Form.Label>
                      <Field
                        name="role"
                        className='form-control'
                        type="text"
                        placeholder="e.g., Product Manager"
                        isInvalid={touched.role && !!errors.role}
                      />
                      <ErrorMessage
                        name="role"
                        component="div"
                        className="text-danger fs--1 mt-1"
                      />
                    </Col>

                    <Col md={12}>
                      <Form.Label className="fw-semibold">
                        Description <span className="text-danger">*</span>
                      </Form.Label>
                      <Field
                        name="text_description"
                        className='form-control'
                        asElement="textarea"
                        placeholder="Share the testimonial text…"
                        rows={4}
                        isInvalid={
                          touched.text_description && !!errors.text_description
                        }
                      />
                      <ErrorMessage
                        name="text_description"
                        component="div"
                        className="text-danger fs--1 mt-1"
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label className="fw-semibold">
                        Status <span className="text-danger">*</span>
                      </Form.Label>
                      <Field
                        name="status"
                        as={Form.Select}
                        isInvalid={touched.status && !!errors.status}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive </option>
                      </Field>
                      <ErrorMessage
                        name="status"
                        component="div"
                        className="text-danger fs--1 mt-1"
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label className="fw-semibold">Order</Form.Label>
                      <Field
                        name="order"
                        className='form-control'
                        type="number"
                        min={0}
                        placeholder="0"
                        isInvalid={touched.order && !!errors.order}
                      />
                      <ErrorMessage
                        name="order"
                        component="div"
                        className="text-danger fs--1 mt-1"
                      />
                    </Col>

                    <Col md={12}>
                      <Form.Label className="fw-semibold">
                        Profile Image
                      </Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.currentTarget.files?.[0] || null;
                          setFieldValue("profile_img", file);
                          setPreview(file ? URL.createObjectURL(file) : null);
                        }}
                        isInvalid={touched.profile_img && !!errors.profile_img}
                      />
                      <ErrorMessage
                        name="profile_img"
                        component="div"
                        className="text-danger fs--1 mt-1"
                      />
                      <div className="mt-2 d-flex align-items-center gap-3">
                        {preview ? (
                          <Image
                            src={preview}
                            thumbnail
                            style={{ maxHeight: 120, objectFit: "cover" }}
                          />
                        ) : null}
                        {values.profile_img && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                              setFieldValue("profile_img", null);
                              setPreview(null);
                            }}
                          >
                            Remove Image
                          </Button>
                        )}
                      </div>
                    </Col>

                    {isSubmitting && (
                      <Col md={12}>
                        <ProgressBar
                          now={progress}
                          label={`${progress}%`}
                          animated
                        />
                      </Col>
                    )}
                  </Row>
                  <div className="text-end">
                    <br />
                    <Button
                      type="submit"
                      variant="primary"
                      className="ms-2"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner
                            size="sm"
                            animation="border"
                            className="me-2"
                          />{" "}
                          Saving…
                        </>
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </div>
                </Modal.Body>
              </FormikForm>
            )}
          </Formik>
        )}

        {payload.loadType === "Update Testimonial" && (
          <Formik
            initialValues={{
              name: payload.name || "",
              role: payload.role || "",
              profile_img: payload.profile_img || null,
              text_description: payload.text_description || "",
              status: payload.status || "inactive",
              order: payload.order || "0",
            }}
            validationSchema={Yup.object({
              name: Yup.string()
                .trim()
                .required("Name is required")
                .max(120, "Max 120 characters"),
              role: Yup.string()
                .trim()
                .max(120, "Max 120 characters")
                .nullable(),
              text_description: Yup.string()
                .trim()
                .required("Description is required")
                .max(2000, "Max 2000 characters"),
              status: Yup.string()
                .oneOf(["active", "inactive"])
                .required("Status is required"),
              order: Yup.number()
                .typeError("Order must be a number")
                .integer("Order must be an integer")
                .min(0, "Minimum 0")
                .nullable(),
              profile_img: Yup.mixed()
                .nullable()
                .test(
                  "fileSize",
                  `File size must be ≤ ${FILE_SIZE_MB}MB`,
                  (file) =>
                    !file || (file && file.size <= FILE_SIZE_MB * 1024 * 1024)
                )
                .test(
                  "fileType",
                  "Only JPG, PNG, WEBP allowed",
                  (file) =>
                    !file || (file && ACCEPTED_TYPES.includes(file.type))
                ),
            })}
            onSubmit={updateTestimonialDB}
          >
            {({ values, setFieldValue, isSubmitting, touched, errors }) => (
              <FormikForm>
                <Modal.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Label className="fw-semibold">
                        Name <span className="text-danger">*</span>
                      </Form.Label>
                      <Field
                        name="name"
                        className='form-control'
                        type="text"
                        placeholder="e.g., Jane Doe"
                        isInvalid={touched.name && !!errors.name}
                      />
                      <ErrorMessage
                        name="name"
                        component="div"
                        className="text-danger fs--1 mt-1"
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label className="fw-semibold">Role</Form.Label>
                      <Field
                        name="role"
                        className='form-control'
                        type="text"
                        placeholder="e.g., Product Manager"
                        isInvalid={touched.role && !!errors.role}
                      />
                      <ErrorMessage
                        name="role"
                        component="div"
                        className="text-danger fs--1 mt-1"
                      />
                    </Col>

                    <Col md={12}>
                      <Form.Label className="fw-semibold">
                        Description <span className="text-danger">*</span>
                      </Form.Label>
                      <Field
                        name="text_description"
                        className='form-control'
                        asElement="textarea"
                        placeholder="Share the testimonial text…"
                        rows={4}
                        isInvalid={
                          touched.text_description && !!errors.text_description
                        }
                      />
                      <ErrorMessage
                        name="text_description"
                        component="div"
                        className="text-danger fs--1 mt-1"
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label className="fw-semibold">
                        Status <span className="text-danger">*</span>
                      </Form.Label>
                      <Field
                        name="status"
                        as={Form.Select}
                        isInvalid={touched.status && !!errors.status}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive </option>
                      </Field>
                      <ErrorMessage
                        name="status"
                        component="div"
                        className="text-danger fs--1 mt-1"
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label className="fw-semibold">Order</Form.Label>
                      <Field
                        name="order"
                        className='form-control'
                        type="number"
                        min={0}
                        placeholder="0"
                        isInvalid={touched.order && !!errors.order}
                      />
                      <ErrorMessage
                        name="order"
                        component="div"
                        className="text-danger fs--1 mt-1"
                      />
                    </Col>

                    <Col md={12}>
                      <Form.Label className="fw-semibold">
                        Profile Image
                      </Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.currentTarget.files?.[0] || null;
                          setFieldValue("profile_img", file);
                          setPreview(file ? URL.createObjectURL(file) : null);
                        }}
                        isInvalid={touched.profile_img && !!errors.profile_img}
                      />
                      <ErrorMessage
                        name="profile_img"
                        component="div"
                        className="text-danger fs--1 mt-1"
                      />
                      <div className="mt-2 d-flex align-items-center gap-3">
                        {preview ? (
                          <Image
                            src={preview}
                            thumbnail
                            style={{ maxHeight: 120, objectFit: "cover" }}
                          />
                        ) : null}
                        {values.profile_img && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                              setFieldValue("profile_img", null);
                              setPreview(null);
                            }}
                          >
                            Remove Image
                          </Button>
                        )}
                      </div>
                    </Col>

                    {isSubmitting && (
                      <Col md={12}>
                        <ProgressBar
                          now={progress}
                          label={`${progress}%`}
                          animated
                        />
                      </Col>
                    )}
                  </Row>
                </Modal.Body>

                <Modal.Footer className="d-flex justify-content-between">
                  <div className="text-muted fs--1">
                    Fields marked <span className="text-danger">*</span> are
                    required.
                  </div>
                  <div>
                    <Button
                      variant="secondary"
                      onClick={handleClose}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      className="ms-2"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner
                            size="sm"
                            animation="border"
                            className="me-2"
                          />{" "}
                          Saving…
                        </>
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </div>
                </Modal.Footer>
              </FormikForm>
            )}
          </Formik>
        )}

        {payload.loadType === "Delete Testimonial" && (
          <div>
            <>
              <p className="text-danger">
                Are you sure you want to delete this testimonial?
                <span className="text-primary">"{payload.name}"</span>?
              </p>
              <div className="text-end">
                <Button
                  variant="danger"
                  className="me-3 "
                  size="md"
                  onClick={deleteEntry}
                  style={{ width: 80 }}
                >
                  Yes
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={handleClose}
                  style={{ width: 80 }}
                >
                  No
                </Button>
              </div>
            </>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default TestimonialsModal;
